import React from 'react';
import { Button, Typography } from 'antd';
import type { LifecycleActionEntryViewV0 } from '@shared/operator-read-models';
import { isBannedAmbiguousPrimaryLabel } from '../actionGrammar';
import {
  PRIMITIVE_AUTHORITY_FLAGS,
  type PrimitivePresentationState,
} from './primitiveSurfaceState';
import { PrimitiveSurfaceFrame } from './PrimitiveSurfaceFrame';
import styles from './primitives.module.css';

const { Text } = Typography;

export interface PrimaryActionAreaProps {
  readonly presentationState: PrimitivePresentationState;
  /** Server-admitted actions only — never decide availability locally. */
  readonly actions?: readonly LifecycleActionEntryViewV0[];
  readonly actionLabels?: Readonly<Record<string, string>>;
  readonly onActionRequest?: (actionKey: string) => void;
  readonly grantsAuthority?: false;
  readonly grantsApproval?: false;
  readonly 'data-testid'?: string;
}

function labelFor(
  action: LifecycleActionEntryViewV0,
  labels: Readonly<Record<string, string>> | undefined
): string {
  return labels?.[action.actionKey] ?? action.actionKey;
}

export function PrimaryActionArea(props: PrimaryActionAreaProps): React.ReactElement {
  const grantsAuthority = props.grantsAuthority ?? PRIMITIVE_AUTHORITY_FLAGS.grantsAuthority;
  const grantsApproval = props.grantsApproval ?? PRIMITIVE_AUTHORITY_FLAGS.grantsApproval;
  if (grantsAuthority !== false || grantsApproval !== false) {
    throw new Error('PrimaryActionArea must keep grantsAuthority/grantsApproval=false');
  }

  const available = (props.actions ?? []).filter((action) => action.status === 'AVAILABLE');

  return (
    <PrimitiveSurfaceFrame
      title="주요 작업"
      presentationState={props.presentationState}
      emptyMessage="서버가 허용한 작업이 없습니다."
      data-testid={props['data-testid'] ?? 'primary-action-area'}
      aria-label="주요 작업"
    >
      {available.length === 0 ? (
        <Text type="secondary">사용 가능한 서버 작업이 없습니다.</Text>
      ) : (
        <div className={styles.primitiveActionStack}>
          {available.map((action, index) => {
            const label = labelFor(action, props.actionLabels);
            if (isBannedAmbiguousPrimaryLabel(label)) {
              throw new Error(`PrimaryActionArea banned label: ${label}`);
            }
            return (
              <Button
                key={action.actionKey}
                type={index === 0 ? 'primary' : 'default'}
                onClick={() => props.onActionRequest?.(action.actionKey)}
              >
                {label}
              </Button>
            );
          })}
        </div>
      )}
    </PrimitiveSurfaceFrame>
  );
}
